import { useState } from 'react';
import { Info, X } from 'lucide-react';
import { Label } from '@paulownia/shared/components/ui/label';
import { Input } from '@paulownia/shared/components/ui/input';
import { Switch } from '@paulownia/shared/components/ui/switch';
import { Button } from '@paulownia/shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';
import type { Product, CompositionComponent } from '@paulownia/shared/types';

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

interface WizardStepCompositionProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  totalRequired: number;
  onTotalRequiredChange: (total: number) => void;
  components: CompositionComponent[];
  onComponentsChange: (components: CompositionComponent[]) => void;
  allProducts: Product[];
}

export function WizardStepComposition({
  enabled,
  onEnabledChange,
  totalRequired,
  onTotalRequiredChange,
  components,
  onComponentsChange,
  allProducts,
}: WizardStepCompositionProps) {
  const [selectKey, setSelectKey] = useState(0);

  // Get products available for composition (exclude already added)
  const availableProductsForComposition = allProducts.filter((p) => {
    if (components.some((c) => c.productId === p.id)) return false;
    return true;
  });

  const addComponent = (productId: string) => {
    const productToAdd = allProducts.find((p) => p.id === productId);
    if (!productToAdd) return;

    const newComponent: CompositionComponent = {
      productId: productToAdd.id,
      label: productToAdd.name,
    };

    onComponentsChange([...components, newComponent]);
    // Reset select to placeholder
    setSelectKey((k) => k + 1);
  };

  const removeComponent = (productId: string) => {
    onComponentsChange(components.filter((c) => c.productId !== productId));
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Coffret composable</h2>
        <p className="text-sm text-gray-500 mt-1">
          Permet de creer un coffret ou le client choisit lui-meme les produits.
        </p>
      </div>

      {/* Info box */}
      <div className="flex gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
        <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium">Les produits du coffret doivent etre crees au prealable.</p>
          <p className="mt-1 text-blue-600">
            Si vous n&apos;avez pas encore cree les produits, vous pouvez sauter cette etape et
            configurer la composition plus tard via l&apos;edition du produit.
          </p>
        </div>
      </div>

      {/* Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg border bg-gray-50">
        <div>
          <Label htmlFor="composition-toggle" className="font-medium">
            Ce produit est un coffret composable
          </Label>
          <p className="text-xs text-gray-500 mt-0.5">
            Le client pourra choisir les produits qui composent ce coffret.
          </p>
        </div>
        <Switch
          id="composition-toggle"
          checked={enabled}
          onCheckedChange={onEnabledChange}
        />
      </div>

      {/* Composition configuration */}
      {enabled && (
        <div className="space-y-4 p-4 rounded-lg border bg-white">
          {/* Total required */}
          <div className="space-y-1">
            <Label className="text-sm">Nombre de produits dans le coffret</Label>
            <Input
              type="number"
              min="1"
              value={totalRequired}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val > 0) {
                  onTotalRequiredChange(val);
                }
              }}
              placeholder="ex: 6, 12, 24..."
              className="h-9 w-32"
            />
            <p className="text-xs text-gray-400">
              Le client devra selectionner exactement ce nombre de produits.
            </p>
          </div>

          {/* List of components */}
          <div className="space-y-2">
            <Label className="text-sm">Produits disponibles dans le coffret</Label>
            {components.length > 0 ? (
              <div className="space-y-1">
                {components.map((comp) => (
                  <div
                    key={comp.productId}
                    className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border"
                  >
                    <span className="text-sm">{comp.label}</span>
                    <button
                      type="button"
                      onClick={() => removeComponent(comp.productId)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic py-2">Aucun produit ajoute</p>
            )}
          </div>

          {/* Add product selector */}
          {availableProductsForComposition.length > 0 ? (
            <div className="pt-2 border-t">
              <Label className="text-xs text-gray-600 mb-1.5 block">Ajouter un produit</Label>
              <Select key={selectKey} onValueChange={addComponent}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Selectionner un produit..." />
                </SelectTrigger>
                <SelectContent>
                  {availableProductsForComposition.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {p.section && (
                        <span className="text-gray-400 ml-2">({capitalize(p.section)})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : allProducts.length === 0 ? (
            <div className="pt-2 border-t">
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                Aucun produit disponible. Creez d&apos;abord les produits qui composeront ce
                coffret.
              </p>
            </div>
          ) : (
            <div className="pt-2 border-t">
              <p className="text-sm text-green-600 bg-green-50 p-3 rounded-lg border border-green-200">
                Tous les produits ont ete ajoutes au coffret.
              </p>
            </div>
          )}

          {/* Remove composition button */}
          <div className="pt-2 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onEnabledChange(false);
                onComponentsChange([]);
                onTotalRequiredChange(1);
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4 mr-1" />
              Desactiver la composition
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
